import React, { useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

import CollectionSelector from "./CollectionSelector";
import Icon from "./Icon";
import Panel from "./Panel";
import Entry from "./QuickCapture/Entry";
import Imports from "./QuickCapture/Imports";
import useClozeSelection from "./QuickCapture/useClozeSelection";

const POLL_INTERVAL_MS = 1000;
const IMPORT_POLL_INTERVAL_MS = 1500;
const IMPORT_DONE_NOTICE_MS = 4000;
const SPEECH_SILENCE_MS = 2000;

// Demo mode timings - the scripted "type a word, watch it generate" sequence
// shown to non-Pro users.
const DEMO_TYPE_MS = 75;
const DEMO_PAUSE_BEFORE_ADD_MS = 450;
const DEMO_GENERATING_MS = 1400;
// The next word starts typing while the previous entry is still generating -
// which is both how you'd really use it and what keeps the demo under ~5s.
const DEMO_PAUSE_BETWEEN_MS = 350;

// Voice input is hidden from users for now - browser speech to text didn't work
// as well as we'd hoped. The implementation below is left in place so we can
// come back to it; flip this to true to bring the mic button back.
const VOICE_INPUT_ENABLED = false;

function shouldPoll(entry) {
  return entry.status === "pending" || entry.status === "queued" || entry.status === "working";
}

function isProcessed(entry) {
  return entry.status === "processed";
}

function isSelectable(entry) {
  return entry.status !== "failed";
}

function importFinished(status) {
  return status === "completed" || status === "completed_with_errors" || status === "failed";
}

// Shapes a demoEntries item ({ word, sentence, sentenceTranslation }) like an
// entry from the API, so the real Entry component renders it unchanged.
function toDemoEntry(demoEntry, index, status) {
  return {
    id: `demo-${index}`,
    sentence: demoEntry.sentence,
    sentenceTranslation: demoEntry.sentenceTranslation,
    status,
    text: demoEntry.word
  };
}

function getSpeechRecognitionCtor() {
  if(typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function QuickCapture(props) {
  const { autoSubmitInitialText, collectionsUrl, demoEntries, entriesUrl, importsUrl, initialText, targetLanguageEnglishName, targetLanguageIso, targetLanguageName } = props;

  // Presence of demoEntries puts the component in demo mode: nothing is
  // fetched or saved, and a scripted capture plays instead.
  const demo = !!(demoEntries && demoEntries.length);

  // initialText lets a caller open Quick Capture already pointed at a word, so
  // you can go straight from seeing it somewhere to capturing it. The demo types
  // its own script, so it always starts empty.
  const [currentText, setCurrentText] = useState(demo ? "" : (initialText || ""));
  const [defaultCollectionId, setDefaultCollectionId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [importing, setImporting] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState(false);
  const [loading, setLoading] = useState(!demo);
  const [notice, setNotice] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedIds, setSelectedIds] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState("capture");
  const [listening, setListening] = useState(false);
  const [demoPlaying, setDemoPlaying] = useState(demo);

  const pollTimers = useRef({});
  const importPollTimer = useRef(null);
  const demoTimers = useRef([]);
  const recognitionRef = useRef(null);
  const speechBaseRef = useRef("");
  const speechSilenceTimer = useRef(null);
  const speechSupported = VOICE_INPUT_ENABLED && !!getSpeechRecognitionCtor();

  const { onSelect: onTextSelect, ref: textInputRef } = useClozeSelection(setCurrentText);

  const stopPolling = (id) => {
    if(pollTimers.current[id]) {
      clearTimeout(pollTimers.current[id]);
      delete pollTimers.current[id];
    }
  };

  const startPolling = (entry) => {
    if(pollTimers.current[entry.id]) return;
    const tick = () => {
      $.ajax({ method: "get", url: entry.url })
        .done((data) => {
          const updated = data.quickCaptureEntry;
          if(!updated) return;
          setEntries((prev) => prev.map((e) => e.id === updated.id ? { ...e, ...updated } : e));
          if(shouldPoll(updated)) {
            pollTimers.current[entry.id] = setTimeout(tick, POLL_INTERVAL_MS);
          }
          else {
            delete pollTimers.current[entry.id];
          }
        })
        .fail(() => {
          pollTimers.current[entry.id] = setTimeout(tick, POLL_INTERVAL_MS * 3);
        });
    };
    pollTimers.current[entry.id] = setTimeout(tick, POLL_INTERVAL_MS);
  };

  const stopImportPolling = () => {
    if(importPollTimer.current) {
      clearTimeout(importPollTimer.current);
      importPollTimer.current = null;
    }
  };

  // Watch an import through to completion so the "Importing..." notice clears
  // itself instead of sitting there until the modal is reopened.
  const pollImport = (importUrl, collectionName) => {
    stopImportPolling();

    const tick = () => {
      $.ajax({ method: "get", url: importUrl })
        .done((data) => {
          const imp = data.quickCaptureImport;
          if(!imp) return;

          if(!importFinished(imp.status)) {
            importPollTimer.current = setTimeout(tick, IMPORT_POLL_INTERVAL_MS);
            return;
          }

          importPollTimer.current = null;

          if(imp.status === "failed") {
            setNotice("Import failed. See the Imports tab for details.");
            return;
          }

          const count = imp.importedCount;
          const done = `Imported ${count} sentence${count === 1 ? "" : "s"} into ${collectionName}.` +
            (imp.failedCount ? ` ${imp.failedCount} failed - see the Imports tab.` : "");
          setNotice(done);

          if(!imp.failedCount) {
            importPollTimer.current = setTimeout(() => {
              importPollTimer.current = null;
              // Don't stomp on a newer notice the user has since triggered.
              setNotice((prev) => prev === done ? null : prev);
            }, IMPORT_DONE_NOTICE_MS);
          }
        })
        .fail(() => {
          importPollTimer.current = setTimeout(tick, IMPORT_POLL_INTERVAL_MS * 2);
        });
    };

    importPollTimer.current = setTimeout(tick, IMPORT_POLL_INTERVAL_MS);
  };

  // Guarded: matchMedia is missing in some test environments and older browsers, and a
  // demo that throws is worse than one that animates.
  const prefersReducedMotion = () => {
    try {
      return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      return false;
    }
  };

  const clearDemoTimers = () => {
    demoTimers.current.forEach((t) => clearTimeout(t));
    demoTimers.current = [];
  };

  // Scripted capture, starting from an empty list: for each demo entry, type
  // its word, drop it in as generating, then reveal the finished sentence.
  //
  // Under prefers-reduced-motion it jumps straight to the finished state instead.
  // A word typing itself one character at a time is precisely what that setting
  // asks us not to do, and the demo's job - showing what capture produces - is
  // done just as well by the end state. Same information, no motion, and still
  // no click required.
  //
  // Read at call time rather than on mount so someone changing the setting and
  // hitting "Watch again" gets what they just asked for.
  const runDemo = () => {
    clearDemoTimers();
    const at = (ms, fn) => demoTimers.current.push(setTimeout(fn, ms));

    setDemoPlaying(true);
    setCurrentText("");
    setSelectedIds({});
    setEntries([]);

    if(prefersReducedMotion()) {
      setEntries(demoEntries.map((e, i) => toDemoEntry(e, i, "processed")).reverse());
      setSelectedIds(demoEntries.reduce((acc, _e, i) => ({ ...acc, [`demo-${i}`]: true }), {}));
      setDemoPlaying(false);
      return;
    }

    let cursor = 0;
    let finishesAt = 0;

    demoEntries.forEach((demoEntry, index) => {
      const word = demoEntry.word || "";
      const id = `demo-${index}`;
      const startedAt = cursor;

      for(let i = 1; i <= word.length; i++) {
        at(startedAt + i * DEMO_TYPE_MS, () => setCurrentText(word.slice(0, i)));
      }

      const addedAt = startedAt + word.length * DEMO_TYPE_MS + DEMO_PAUSE_BEFORE_ADD_MS;

      at(addedAt, () => {
        setCurrentText("");
        setEntries((prev) => [toDemoEntry(demoEntry, index, "working"), ...prev]);
      });

      at(addedAt + DEMO_GENERATING_MS, () => {
        setEntries((prev) => prev.map((e) => (
          e.id === id ? { ...e, status: "processed" } : e
        )));
        setSelectedIds((prev) => ({ ...prev, [id]: true }));
      });

      finishesAt = Math.max(finishesAt, addedAt + DEMO_GENERATING_MS);
      cursor = addedAt + DEMO_PAUSE_BETWEEN_MS;
    });

    at(finishesAt, () => setDemoPlaying(false));
  };

  useEffect(() => {
    if(demo) {
      setLoading(false);
      runDemo();
    }
    else {
      $.ajax({ method: "get", url: entriesUrl })
        .done((data) => {
          const loaded = data.quickCaptureEntries || [];
          setDefaultCollectionId(data.defaultCollection && data.defaultCollection.id);
          // Merge rather than replace: an auto-submitted capture can land
          // before this index request does, and replacing would drop it along
          // with its polling registration.
          setEntries((prev) => {
            const seen = new Set(loaded.map((e) => e.id));
            return [...prev.filter((e) => !seen.has(e.id)), ...loaded];
          });
          setLoading(false);
          loaded.forEach((e) => { if(shouldPoll(e)) startPolling(e); });
        })
        .fail(() => {
          setLoading(false);
          setInitialLoadError(true);
        });
    }

    return () => {
      clearDemoTimers();
      stopImportPolling();
      Object.values(pollTimers.current).forEach((t) => clearTimeout(t));
      pollTimers.current = {};
      if(recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch(_e) { /* ignore */ }
        recognitionRef.current = null;
      }
      if(speechSilenceTimer.current) {
        clearTimeout(speechSilenceTimer.current);
        speechSilenceTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSilenceTimer = () => {
    if(speechSilenceTimer.current) {
      clearTimeout(speechSilenceTimer.current);
      speechSilenceTimer.current = null;
    }
  };

  const armSilenceTimer = () => {
    clearSilenceTimer();
    speechSilenceTimer.current = setTimeout(() => {
      if(recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch(_e) { /* ignore */ }
      }
    }, SPEECH_SILENCE_MS);
  };

  const stopListening = () => {
    clearSilenceTimer();
    if(recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch(_e) { /* ignore */ }
    }
  };

  const startListening = () => {
    const Ctor = getSpeechRecognitionCtor();
    if(!Ctor) return;

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    console.log(targetLanguageIso);
    if(targetLanguageIso) recognition.lang = targetLanguageIso;

    speechBaseRef.current = currentText ? currentText.replace(/\s+$/, "") + " " : "";

    recognition.onresult = (event) => {
      let interim = "";
      for(let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if(event.results[i].isFinal) {
          speechBaseRef.current = (speechBaseRef.current + transcript).replace(/\s+$/, "") + " ";
        }
        else {
          interim += transcript;
        }
      }
      setCurrentText((speechBaseRef.current + interim).replace(/\s+/g, " "));
      armSilenceTimer();
    };

    recognition.onerror = (event) => {
      if(event.error === "not-allowed" || event.error === "service-not-allowed") {
        setNotice("Microphone access denied. Enable it in your browser settings to use speech input.");
      }
      else if(event.error === "no-speech") {
        setNotice("Didn't catch that. Try speaking again.");
      }
      else if(event.error !== "aborted") {
        setNotice(`Speech recognition error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      clearSilenceTimer();
      setListening(false);
      setCurrentText(speechBaseRef.current.replace(/\s+$/, ""));
      recognitionRef.current = null;
      if(textInputRef.current) textInputRef.current.focus();
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
      setNotice(null);
      armSilenceTimer();
    }
    catch(e) {
      setListening(false);
      setNotice("Could not start speech recognition. Please try again.");
    }
  };

  const toggleListening = () => {
    if(listening) stopListening();
    else startListening();
  };

  // Opening already pointed at a word is one thing; capturing it without being
  // asked is another, so it's opt in. Fires once per mount - the caller remounts
  // per word - and never in demo mode.
  const autoSubmitted = useRef(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if(demo) return;
    const text = currentText.trim();
    if(!text) return;

    setSubmitting(true);
    setNotice(null);

    $.ajax({
      contentType: "application/json",
      data: JSON.stringify({ quick_capture_entry: { text } }),
      dataType: "json",
      method: "post",
      url: entriesUrl
    })
      .done((data) => {
        if(data.errors) {
          setSubmitting(false);
          setNotice(data.errors.join(", "));
          return;
        }
        const entry = data.quickCaptureEntry;
        setEntries((prev) => {
          const exists = prev.some((e) => e.id === entry.id);
          return exists
            ? prev.map((e) => e.id === entry.id ? { ...e, ...entry } : e)
            : [entry, ...prev];
        });
        // Select new entries by default so they can be imported right away.
        setSelectedIds((prev) => ({ ...prev, [entry.id]: true }));
        setCurrentText("");
        setSubmitting(false);
        if(shouldPoll(entry)) startPolling(entry);
        if(textInputRef.current) textInputRef.current.focus();
      })
      .fail(() => {
        setSubmitting(false);
        setNotice("Failed to capture. Please try again.");
      });
  };

  useEffect(() => {
    if(!autoSubmitInitialText || demo || autoSubmitted.current) return;
    if(!initialText || !initialText.trim()) return;

    autoSubmitted.current = true;
    // onSubmit is written as a form handler; there's no real event here.
    onSubmit({ preventDefault: () => {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpdateEntry = (updated) => {
    setEntries((prev) => prev.map((e) => e.id === updated.id ? { ...e, ...updated } : e));
  };

  const onDeleteEntry = (id) => {
    const entry = entries.find((e) => e.id === id);
    if(!entry) return;

    setEntries((prev) => prev.filter((e) => e.id !== id));
    setSelectedIds((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    stopPolling(id);

    $.ajax({ method: "delete", url: entry.url })
      .fail(() => {
        setEntries((prev) => prev.some((e) => e.id === entry.id) ? prev : [entry, ...prev]);
        setNotice("Failed to delete. Please try again.");
      });
  };

  const onToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      if(next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const selectableEntries = entries.filter(isSelectable);
  const selectedEntries = selectableEntries.filter((e) => selectedIds[e.id]);
  const importableIds = selectedEntries.filter(isProcessed).map((e) => e.id);
  const generatingCount = selectedEntries.length - importableIds.length;
  const allSelected = selectableEntries.length > 0 && selectedEntries.length === selectableEntries.length;

  const onToggleSelectAll = () => {
    if(allSelected) {
      setSelectedIds({});
      return;
    }
    const next = {};
    selectableEntries.forEach((e) => { next[e.id] = true; });
    setSelectedIds(next);
  };

  const onImport = (idsOverride) => {
    const ids = idsOverride || importableIds;

    if(!ids.length) {
      setNotice(generatingCount
        ? "Still generating sentences - try again in a moment."
        : "Select at least one entry to import.");
      return;
    }
    if(!selectedCollection) {
      setNotice("Choose a collection first.");
      return;
    }

    setImporting(true);
    setNotice(null);

    $.ajax({
      contentType: "application/json",
      data: JSON.stringify({ quick_capture_entry_ids: ids, collection_id: selectedCollection.id }),
      dataType: "json",
      method: "post",
      url: importsUrl
    })
      .done((data) => {
        if(data.errors) {
          setImporting(false);
          setNotice(data.errors.join(", "));
          return;
        }
        const removeSet = {};
        ids.forEach((id) => { removeSet[id] = true; stopPolling(id); });
        setEntries((prev) => prev.filter((e) => !removeSet[e.id]));
        setSelectedIds({});
        setImporting(false);
        setNotice(`Importing ${ids.length} entr${ids.length === 1 ? "y" : "ies"} into ${selectedCollection.name}.`);
        const imp = data.quickCaptureImport;
        if(imp && imp.url) pollImport(imp.url, selectedCollection.name);
      })
      .fail(() => {
        setImporting(false);
        setNotice("Import failed to start. Please try again.");
      });
  };

  const renderInput = () => {
    const placeholder = targetLanguageName
      ? `Type a word or phrase in ${targetLanguageName}`
      : "Type a word or phrase";

    const speakLabel = targetLanguageName ? `Speak in ${targetLanguageName}` : "Speak";
    const speakTitle = `${speakLabel}. Tip: speaking full sentences works best.`;

    return (
      <form onSubmit={onSubmit}>
        <div className="form-group">
          {speechSupported && (
            <div style={{ marginBottom: 8 }}>
              <button
                className={`btn btn-sm ${listening ? "btn-danger" : "btn-default"}`}
                onClick={toggleListening}
                title={speakTitle}
                type="button"
              >
                <Icon name={listening ? "stop" : "microphone"} type={listening ? undefined : "fa"} />
                {" "}
                {listening ? "Stop" : speakLabel}
              </button>
              {listening && (
                <span className="text-muted small" style={{ marginLeft: 10 }}>
                  Listening... speak a full sentence for best results.
                </span>
              )}
            </div>
          )}
          <TextareaAutosize
            autoFocus={!demo}
            className="form-control"
            disabled={submitting}
            maxLength={200}
            minRows={1}
            onChange={(e) => setCurrentText(e.target.value)}
            onKeyDown={(e) => {
              if(e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if(!submitting && currentText.trim()) onSubmit(e);
              }
            }}
            onSelect={onTextSelect}
            placeholder={placeholder}
            readOnly={demo}
            ref={textInputRef}
            value={currentText}
          />
          <p className="help-block small">
            Highlight text to use as the cloze, or skip and we&apos;ll choose. Wraps as <code>{"{{...}}"}</code>.
          </p>
        </div>
        <button
          className="btn btn-success joystix"
          disabled={demo || submitting || !currentText.trim()}
          type="submit"
        >
          <Icon name="plus" /> Add
        </button>
        {demo && !demoPlaying && (
          <button className="btn btn-link" onClick={runDemo} type="button">
            <Icon name="refresh" /> Watch again
          </button>
        )}
      </form>
    );
  };

  const renderEntries = () => {
    if(loading) return <div className="text-center"><Icon name="refresh" /> Loading...</div>;
    if(initialLoadError) return <div className="alert alert-danger">Failed to load entries.</div>;
    if(!entries.length) {
      if(demo) return null;
      return (
        <div className="well well-sm">
          <strong>How it works</strong>
          <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li>Type {speechSupported ? "or speak " : ""}a word, phrase, or sentence above.</li>
            <li>Optionally highlight the word you want as the cloze if you're adding a phrase or sentence.</li>
            <li>We&apos;ll generate an example sentence if needed, and add a translation.</li>
            <li>Later when you're ready, pick a collection and click <strong>Import</strong> to add all your Quick Capture entries to a collection to play.</li>
          </ol>
        </div>
      );
    }

    return (
      <>
        {!demo && !!selectableEntries.length && (
          <div style={{ marginBottom: 8 }}>
            <button className="btn btn-link btn-xs" onClick={onToggleSelectAll} type="button">
              {allSelected ? "Deselect all" : "Select all"}
            </button>
          </div>
        )}
        <ul className="list-group">
          {entries.map((entry) => (
            <Entry
              entry={entry}
              key={entry.id}
              onDelete={onDeleteEntry}
              onImportSingle={(id) => onImport([id])}
              onToggleSelect={onToggleSelect}
              onUpdate={onUpdateEntry}
              readOnly={demo}
              selected={!!selectedIds[entry.id]}
              targetLanguageIso={targetLanguageIso}
            />
          ))}
        </ul>
      </>
    );
  };

  const renderActions = () => {
    if(!entries.length) return null;

    if(demo) {
      return (
        <Panel style={{ marginTop: 15 }}>
          <div className="text-muted">
            <Icon name="lock" /> Then pick a collection, hit Import, and play them
            {targetLanguageEnglishName ? ` in ${targetLanguageEnglishName}` : ""} right away.
          </div>
        </Panel>
      );
    }

    const selectedCount = selectedEntries.length;

    return (
      <Panel style={{ marginTop: 15 }}>
        <CollectionSelector
          collectionsUrl={collectionsUrl}
          initialSelectedCollectionId={defaultCollectionId}
          onSelect={setSelectedCollection}
        />
        <div style={{ marginTop: 10 }}>
          <button
            className="btn btn-success joystix"
            disabled={importing || !selectedCount || !!generatingCount || !selectedCollection}
            onClick={() => onImport()}
            type="button"
          >
            {importing ? "Importing..." : `Import (${selectedCount})`}
          </button>
          {!!generatingCount && (
            <span className="text-muted small" style={{ marginLeft: 10 }}>
              Still generating {generatingCount} selected entr{generatingCount === 1 ? "y" : "ies"}...
            </span>
          )}
        </div>
      </Panel>
    );
  };

  const renderNotice = () => {
    if(!notice) return null;
    return (
      <div className="alert alert-info" style={{ marginBottom: 15 }}>
        <button className="close" onClick={() => setNotice(null)} type="button">
          <span aria-hidden="true">&times;</span>
        </button>
        {notice}
      </div>
    );
  };

  const renderViewToggle = () => (
    <ul className="nav nav-tabs quick-capture-tabs" style={{ marginBottom: 15 }}>
      <li className={view === "capture" ? "active" : ""}>
        <a href="#" onClick={(e) => { e.preventDefault(); setView("capture"); }}>Capture</a>
      </li>
      <li className={view === "imports" ? "active" : ""}>
        <a href="#" onClick={(e) => { e.preventDefault(); setView("imports"); }}>Imports</a>
      </li>
    </ul>
  );

  return (
    <div>
      {!demo && (
        <p>
          Quickly and easily capture words and phrases as you encounter them in the real world - reading books, watching TV, etc. We&apos;ll auto-generate an example sentence (unless you're adding a full sentence) and translation, then you can import them later into a collection to play.
        </p>
      )}
      {!demo && renderViewToggle()}
      {renderNotice()}
      {view === "capture" && (
        <>
          {renderInput()}
          <hr />
          {renderEntries()}
          {renderActions()}
        </>
      )}
      {view === "imports" && (
        <Imports importsUrl={importsUrl} />
      )}
    </div>
  );
}
